import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/i18n";
import { X, Sparkles, Copy, Check } from "lucide-react";

export const NewsletterPopup = () => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (localStorage.getItem("newsletter_seen")) return;
    const timer = setTimeout(() => setOpen(true), 6000);
    return () => clearTimeout(timer);
  }, []);

  const close = () => { localStorage.setItem("newsletter_seen", "1"); setOpen(false); };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const { data } = await api.post("/newsletter/subscribe", { email });
      setCode(data.code);
      localStorage.setItem("newsletter_seen", "1");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const copy = () => { navigator.clipboard?.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 1500); };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 px-4" data-testid="newsletter-popup">
      <div className="relative bg-[#FAF9F6] max-w-md w-full p-10 border border-[#EAE6E1]">
        <button onClick={close} data-testid="newsletter-close" className="absolute top-4 right-4 text-[#6B7280] hover:text-[#1A1F2C]">
          <X className="w-5 h-5" />
        </button>
        <Sparkles className="w-7 h-7 text-[#C5A880] mb-4" strokeWidth={1.3} />

        {code ? (
          <div data-testid="newsletter-success">
            <h2 className="font-serif-display text-3xl text-[#1A1F2C] mb-3">{t("newsletter_success")}</h2>
            <div className="flex items-center justify-between bg-white border border-[#C5A880] px-5 py-4 mt-4">
              <span className="text-xl tracking-[0.2em] text-[#1A1F2C]">{code}</span>
              <button onClick={copy} className="text-[#C5A880] hover:text-[#1A1F2C]">
                {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
              </button>
            </div>
            <button onClick={close} className="mt-6 w-full bg-[#1A1F2C] text-white text-xs tracking-luxe uppercase py-3.5 hover:bg-[#C5A880] transition-colors">
              {t("close")}
            </button>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="newsletter-form">
            <h2 className="font-serif-display text-3xl text-[#1A1F2C] mb-2">{t("newsletter_title")}</h2>
            <p className="text-sm text-[#6B7280] mb-6">{t("newsletter_sub")}</p>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("newsletter_placeholder")}
              data-testid="newsletter-email"
              className="w-full bg-white border border-[#EAE6E1] px-4 py-3 text-sm focus:outline-none focus:border-[#C5A880]" />
            {error && <p className="text-sm text-[#991B1B] mt-2">{error}</p>}
            <button type="submit" disabled={loading} data-testid="newsletter-submit"
              className="mt-4 w-full bg-[#1A1F2C] text-white text-xs tracking-luxe uppercase py-3.5 hover:bg-[#C5A880] transition-colors disabled:opacity-60">
              {loading ? "…" : t("newsletter_cta")}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};
